/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ChevronLeft from '@icon/ChevronLeft';
import ChevronRight from '@icon/ChevronRight';
import P1 from '@base/P1/P1';
import EmptyState from '@comp/EmptyState/EmptyState';
import Drawer from '@base/Drawer/Drawer';
import Menu from '@base/Menu/Menu';
import ScheduleCalendar from '@base/ScheduleCalendar/ScheduleCalendar';
import Dialog from '@comp/Dialog/Dialog';
import ShareDialog from '@comp/ShareDialog/ShareDialog';
import { useSelectorToStore, useDispatchToStore } from '@hooks/reduxHooks';
import { fetchEductatorScheduledClasses } from '@actions/educator';
import RescheduleLesson from '@educator/Container/CourseDetails/RescheduleLesson';
import pushRoute from '@utils/pushRoute';
import textHelper from '@utils/textHelper';
import ClassesList from './ClassesList';
import {
  Spacings,
  BgColors,
  TextColors,
  BorderColors
} from '../../../../styleConstants';
import { programmeTypes } from '../../../../constants';

const DaysOfTheWeek = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const MenuItem = styled.li`
  display: flex;
  align-items: center;
  padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
  cursor: pointer;
`;

const CalendarWrapper = styled.div`
  position: relative;
  grid-column: span 7;
`;

const MissedDot = styled.div`
  background-color: ${BgColors.RED};
  height: 4px;
  width: 4px;
  border-radius: 2px;
  margin: auto;
`;

const Calendar = styled(ScheduleCalendar)`
  .react-calendar__tile--active {
    background: ${BgColors.BLUE} !important;
  }
  .react-calendar__tile.react-calendar__tile--now {
    color: ${TextColors.BLUE} !important;
    border: 1px solid ${BorderColors.BLUE};
    border-radius: 50%;
  }
`;

const formatShortWeekday = (locale, date) => DaysOfTheWeek[date.getDay()];

const MySchedule = ({ goalUID }) => {
  const dispatch = useDispatchToStore();
  const [rescheduleDrawer, setRescheduleDrawer] = useState(false);

  const props = useSelectorToStore(
    ({ paginator, topologies, auth, player, items }) => {
      return {
        schedule: paginator.eduScheduledClasses,
        calendarData: items.calendarData,
        goalColor: topologies?.data?.[goalUID]?.colorCode || '#EBD3A7',
        me: auth.me,
        selectedGoal: auth.selectedGoal,
        renewalData: auth.renewalData?.[goalUID],
        unLiveURL: player.unLiveURL
      };
    }
  );

  const {
    schedule,
    calendarData,
    goalColor,
    me,
    selectedGoal,
    renewalData,
    unLiveURL
  } = props;
  const currentMonthStartDate = useRef();
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentSession, setCurrentSession] = useState(null);
  const [typeOfClass, setTypeOfClass] = useState({
    isUpcoming: false,
    isLive: false,
    isCompleted: false,
    isMissed: false
  });

  const refDate = new Date();
  refDate.setHours(0, 0, 0, 0);
  const [datePointer, setDatePointer] = useState(refDate.getTime());
  const [currentMonthPointer, setCurrentMonthPointer] = useState(
    refDate.getMonth()
  );

  const dispatchFetchScheduleData = (startEpoch, reset = false) => {
    if (startEpoch) {
      dispatch(fetchEductatorScheduledClasses(startEpoch, false, reset));
    }
  };

  // didMount
  useEffect(() => {
    dispatchFetchScheduleData(datePointer, false);
    window.scrollTo(0, 0);
  }, [dispatch, goalUID]);

  useEffect(() => {
    if (new Date(datePointer).getMonth() !== currentMonthPointer) {
      setCurrentMonthPointer(new Date(datePointer).getMonth());
      currentMonthStartDate.current = new Date(datePointer);
    }
  }, [datePointer]);

  const onDateSelect = (date) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    const startEpoch = newDate.getTime();
    window.scrollTo({ top: '112px' });
    setDatePointer(startEpoch);
    dispatch(fetchEductatorScheduledClasses(startEpoch, true, true));
  };

  const onMonthNavigationClick = ({ activeStartDate }) => {
    setCurrentMonthPointer(activeStartDate.getMonth());
    currentMonthStartDate.current = activeStartDate;
  };

  const setMissedClasses = ({ date, view }) => {
    let isMissedClass = false;
    if (view === 'month' && calendarData[currentMonthPointer]) {
      const missedClass =
        calendarData[currentMonthPointer].count?.filter(
          (item) =>
            Number(item.epoch) ===
              Math.round(new Date(date).getTime() / 1000) &&
            item.missed > 0 &&
            Number(item.epoch) !== Math.round(datePointer / 1000)
        ) || [];
      isMissedClass = missedClass.length > 0;
      if (isMissedClass) return <MissedDot />;
    }
  };

  const setClassStylings = ({ date, view }) => {
    let isNoClassDayAttr = false;
    if (view === 'month' && calendarData[currentMonthPointer]) {
      const noClassDay =
        calendarData[currentMonthPointer].count?.filter(
          (item) =>
            Number(item.epoch) ===
              Math.round(new Date(date).getTime() / 1000) &&
            item.missed === 0 &&
            item.upcoming === 0 &&
            item.completed === 0 &&
            Number(item.epoch) !== Math.round(datePointer / 1000)
        ) || [];
      isNoClassDayAttr = noClassDay?.length > 0;
      if (isNoClassDayAttr) return 'disabledClass';
    }
    return null;
  };

  const renderInfiniteList = (scheduleClasses) => {
    if (!scheduleClasses) return null;
    return (
      <ClassesList
        schedule={scheduleClasses}
        dispatchFetchScheduleData={dispatchFetchScheduleData}
        setDatePointer={setDatePointer}
        datePointer={datePointer}
        goalColor={goalColor}
        me={me}
        selectedGoal={selectedGoal}
        renewalData={renewalData}
        unLiveURL={unLiveURL}
        setAnchorEl={setAnchorEl}
        setTypeOfClass={setTypeOfClass}
        setCurrentSession={setCurrentSession}
      />
    );
  };

  const handleClose = () => setAnchorEl(null);

  const rescheduleClass = () => {
    setRescheduleDrawer(true);
    handleClose();
  };

  const editClass = () => {
    const {
      is_special: isSpecial,
      programme_type: programmeType,
      uid: courseUID
    } = currentSession.programmeNM;
    if (isSpecial)
      pushRoute(
        '/educator/[courseType]/edit/[courseUID]',
        `/educator/special-classes/edit/${courseUID}`
      );
    else
      pushRoute(
        {
          pathname: '/educator/[courseType]/edit/[courseUID]',
          query: { editClassUID: currentSession.uid }
        },
        `/educator/${
          programmeType === programmeTypes.COURSE
            ? 'plus-courses'
            : 'test-series'
        }/edit/${courseUID}?editClassUID=${currentSession.uid}`
      );
    handleClose();
  };

  const viewStats = () => {
    const {
      is_special: isSpecial,
      programme_type: programmeType,
      uid: courseUID,
      name: courseName
    } = currentSession?.programmeNM || {};
    const slugCourseName = textHelper.getStringWordsJoinedWith(courseName, '-');

    handleClose();
    let query = { lesson: `${currentSession.type}_${currentSession?.uid}` };
    let pathname = '/educator/[courseType]/[courseSlug]/[courseUID]';

    let progString = 'plus-courses';
    if (programmeType === programmeTypes.COURSE && isSpecial) {
      progString = 'class';
      query = {};
      pathname = '/educator/class/[slug]/[courseUID]';
    } else if (programmeType === programmeTypes.TESTSERIES)
      progString = 'test-series';

    const as = `/educator/${progString}/${slugCourseName}/${courseUID}${
      programmeType === programmeTypes.COURSE && isSpecial
        ? ''
        : `?lesson=${currentSession.type}_${currentSession?.uid}`
    } `;

    const href = {
      pathname,
      query
    };
    pushRoute(href, as);
  };

  const handleShare = () => {
    setShowShareOptions(true);
    handleClose();
  };

  const closeShareOptiopns = () => setShowShareOptions(false);
  const { isModerator } = me;
  const options = [];
  if (typeOfClass.isUpcoming) {
    options.push({ label: 'Reschedule', onClick: rescheduleClass });
    if (isModerator) options.push({ label: 'Edit', onClick: editClass });
    options.push({ label: 'Share', onClick: handleShare });
  } else if (typeOfClass.isLive) {
    options.push({ label: 'Reschedule', onClick: rescheduleClass });
    if (isModerator) options.push({ label: 'Edit', onClick: editClass });
    options.push({ label: 'Share', onClick: handleShare });
  } else if (typeOfClass.isCompleted) {
    options.push({ label: 'Share', onClick: handleShare });
    options.push({ label: 'View Stats', onClick: viewStats });
  } else if (typeOfClass.isMissed) {
    options.push({ label: 'Reschedule', onClick: rescheduleClass });
  }
  let sharePermaLink = null;
  if (currentSession) {
    const { is_special: isSpecial } = currentSession.programmeNM;
    if (isSpecial)
      sharePermaLink = `https://unacademy.com/class/${currentSession?.slug}/${currentSession?.uid}`;
    else sharePermaLink = currentSession.permalink;
  }

  const shareDialogData = { name: 'unacademy', permalink: sharePermaLink };
  const closeRescheduleDrawer = () => {
    setRescheduleDrawer(false);
  };
  return (
    <>
      <Drawer drawerState={rescheduleDrawer} onClose={closeRescheduleDrawer}>
        <RescheduleLesson
          lesson={currentSession}
          setRescheduleDrawer={setRescheduleDrawer}
          isSchedulePage
          datePointer={datePointer}
        />
      </Drawer>
      <Dialog onClose={closeShareOptiopns} dialogState={showShareOptions}>
        <ShareDialog data={shareDialogData} useDataLink />
      </Dialog>
      <Menu
        anchorEl={anchorEl}
        onClose={handleClose}
        position="right"
        width="221px"
      >
        {options.map((option) => (
          <MenuItem key={option.label} onClick={option.onClick}>
            <P1>{option.label}</P1>
          </MenuItem>
        ))}
      </Menu>
      {schedule?.scheduledClasses?.results?.length === 0 &&
      schedule?.scheduledClasses?.loaded ? (
        <EmptyState title="No classes found" gridColumn="span 10" />
      ) : (
        renderInfiniteList(schedule?.scheduledClasses)
      )}
      <CalendarWrapper>
        <Calendar
          value={new Date(datePointer)}
          defaultActiveStartDate={new Date()}
          activeStartDate={currentMonthStartDate.current}
          locale="en-US"
          calendarType="US"
          formatShortWeekday={formatShortWeekday}
          prevLabel={<ChevronLeft size={Spacings.SPACING_5B} />}
          nextLabel={<ChevronRight size={Spacings.SPACING_5B} />}
          minDetail="month"
          next2Label={null}
          prev2Label={null}
          tileContent={setMissedClasses}
          tileClassName={setClassStylings}
          onClickDay={onDateSelect}
          onActiveStartDateChange={onMonthNavigationClick}
        />
      </CalendarWrapper>
    </>
  );
};

export default MySchedule;

MySchedule.propTypes = {
  schedule: PropTypes.arrayOf(PropTypes.any),
  goalUID: PropTypes.string,
  calendarData: PropTypes.objectOf(PropTypes.any),
  goalColor: PropTypes.string.isRequired,
  me: PropTypes.objectOf(PropTypes.any),
  selectedGoal: PropTypes.objectOf(PropTypes.any),
  renewalData: PropTypes.objectOf(PropTypes.any),
  unLiveURL: PropTypes.string.isRequired
};

MySchedule.defaultProps = {
  schedule: null,
  goalUID: null,
  calendarData: {},
  me: {},
  selectedGoal: null,
  renewalData: {}
};
